import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import type { DocumentationVersion } from '@/services/admin/documentationService'
import { useTranslation } from 'react-i18next'

interface DocumentationContentVersionsTabProps {
  versionStatusOptions: Array<{ value: string; label: string }>
  versionStatus: string
  createVersionCode: string
  createVersionLabel: string
  createSourceRef: string
  versionRows: DocumentationVersion[]
  versionsLoading: boolean
  anyVersionPending: boolean
  publishPending: boolean
  archivePending: boolean
  deletePending: boolean
  setDefaultPending: boolean
  createPending: boolean
  onSetVersionStatus: (value: string) => void
  onSetCreateVersionCode: (value: string) => void
  onSetCreateVersionLabel: (value: string) => void
  onSetCreateSourceRef: (value: string) => void
  onCreateDraft: () => void
  onOpenPublishConfirm: (versionCode: string, setDefault: boolean) => void
  onOpenSetDefaultConfirm: (versionCode: string) => void
  onOpenArchiveConfirm: (row: DocumentationVersion) => void
  onOpenDeleteConfirm: (row: DocumentationVersion) => void
  statusColor: (status: string) => string
  formatDateTime: (value?: string | null) => string
}

export function DocumentationContentVersionsTab({
  versionStatusOptions,
  versionStatus,
  createVersionCode,
  createVersionLabel,
  createSourceRef,
  versionRows,
  versionsLoading,
  anyVersionPending,
  publishPending,
  archivePending,
  deletePending,
  setDefaultPending,
  createPending,
  onSetVersionStatus,
  onSetCreateVersionCode,
  onSetCreateVersionLabel,
  onSetCreateSourceRef,
  onCreateDraft,
  onOpenPublishConfirm,
  onOpenSetDefaultConfirm,
  onOpenArchiveConfirm,
  onOpenDeleteConfirm,
  statusColor,
  formatDateTime,
}: DocumentationContentVersionsTabProps) {
  const { t } = useTranslation()

  return (
    <VStack align="stretch" spacing={4}>
      <Box borderWidth="1px" borderRadius="md" p={3}>
        <HStack justify="space-between" mb={3} spacing={3} flexWrap="wrap">
          <Text fontWeight="semibold">{t('documentationContent.versions.createDraftTitle')}</Text>
          <HStack spacing={3} flexWrap="wrap">
            <Box w="220px">
              <DropdownSelect
                size="sm"
                value={versionStatus}
                options={versionStatusOptions}
                onChange={(value) => onSetVersionStatus(value)}
              />
            </Box>
          </HStack>
        </HStack>
        <HStack spacing={3} align="stretch" flexWrap="wrap">
          <Input
            maxW="220px"
            placeholder={t('documentationContent.versions.versionCodePlaceholder')}
            value={createVersionCode}
            onChange={(event) => onSetCreateVersionCode(event.target.value)}
          />
          <Input
            maxW="220px"
            placeholder={t('documentationContent.versions.versionLabelPlaceholder')}
            value={createVersionLabel}
            onChange={(event) => onSetCreateVersionLabel(event.target.value)}
          />
          <Input
            maxW="320px"
            placeholder={t('documentationContent.versions.sourceRefPlaceholder')}
            value={createSourceRef}
            onChange={(event) => onSetCreateSourceRef(event.target.value)}
          />
          <Button colorScheme="blue" onClick={onCreateDraft} isLoading={createPending}>
            {t('common.create')}
          </Button>
        </HStack>
      </Box>

      {versionsLoading ? (
        <HStack py={8} justify="center">
          <Spinner />
          <Text>{t('documentationContent.versions.loadingVersions')}</Text>
        </HStack>
      ) : (
        <Box overflowX="auto" borderWidth="1px" borderRadius="md">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('documentationContent.versions.version')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('documentationContent.versions.default')}</Th>
                <Th isNumeric>{t('documentationContent.versions.pages')}</Th>
                <Th>{t('documentationContent.versions.publishedAt')}</Th>
                <Th>{t('common.updatedAt')}</Th>
                <Th>{t('documentationContent.sync.sourceRef')}</Th>
                <Th>{t('common.actions')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {versionRows.map((row) => (
                <Tr key={`${row.contentType}:${row.versionCode}`}>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="semibold">{row.versionCode}</Text>
                      <Text fontSize="xs" color="gray.500">{row.versionLabel}</Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Badge colorScheme={statusColor(row.status)}>
                      {t(`documentationContent.statuses.${row.status}`, {
                        defaultValue: row.status,
                      })}
                    </Badge>
                  </Td>
                  <Td>
                    {row.isDefault ? (
                      <Badge colorScheme="green">{t('documentationContent.versions.default')}</Badge>
                    ) : (
                      <Text color="gray.500">{t('common.no')}</Text>
                    )}
                  </Td>
                  <Td isNumeric>{row.pageCount}</Td>
                  <Td>{formatDateTime(row.publishedAt)}</Td>
                  <Td>{formatDateTime(row.updatedAt)}</Td>
                  <Td>
                    <Text maxW="280px" noOfLines={1} title={row.sourceRef}>
                      {row.sourceRef || t('common.na')}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      {row.status === 'draft' ? (
                        <>
                          <Button
                            size="xs"
                            colorScheme="blue"
                            onClick={() => onOpenPublishConfirm(row.versionCode, false)}
                            isLoading={publishPending}
                            isDisabled={anyVersionPending}
                          >
                            {t('documentationContent.versions.publish')}
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="purple"
                            onClick={() => onOpenPublishConfirm(row.versionCode, true)}
                            isLoading={publishPending}
                            isDisabled={anyVersionPending}
                          >
                            {t('documentationContent.versions.publishAndDefault')}
                          </Button>
                        </>
                      ) : null}

                      {row.status === 'published' && !row.isDefault ? (
                        <Button
                          size="xs"
                          colorScheme="teal"
                          onClick={() => onOpenSetDefaultConfirm(row.versionCode)}
                          isLoading={setDefaultPending}
                          isDisabled={anyVersionPending}
                        >
                          {t('documentationContent.versions.setDefault')}
                        </Button>
                      ) : null}

                      {row.status !== 'archived' ? (
                        <Button
                          size="xs"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => onOpenArchiveConfirm(row)}
                          isLoading={archivePending}
                          isDisabled={anyVersionPending}
                        >
                          {t('documentationContent.versions.archive')}
                        </Button>
                      ) : null}

                      <Button
                        size="xs"
                        colorScheme="red"
                        onClick={() => onOpenDeleteConfirm(row)}
                        isLoading={deletePending}
                        isDisabled={anyVersionPending}
                      >
                        {t('common.delete')}
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </VStack>
  )
}
